import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DotLottie, DotLottieReact } from "@lottiefiles/dotlottie-react";
import logoAnimation from "@/assets/lottie/cash-ctrl-logo.lottie";
import choicesAnimation from "@/assets/lottie/intro-choices-animation.lottie";
import Button from "@/components/form/Button";
import BatteryIcon from "@/components/icons/BatteryIcon";
import WifiIcon from "@/components/icons/WifiIcon";
import CashCtrlWordmark from "@/components/logos/CashCtrlWordmark";
import { InitialRoomSelect } from "@/components/screens/SplashScreen/components/IntroSequence/components/WelcomeSequence/components/InitialRoomSelect";

const TOTAL_STEPS = 3;

const slidePositionTransitionProps = {
    type: "tween",
    duration: 0.5,
    ease: [0.7, 0, 0.3, 1],
} as const;

const WelcomeSequence = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [lottieComplete, setLottieComplete] = useState(false);
    const [roomSelected, setRoomSelected] = useState(false);
    const [isRoomTransitioning, setIsRoomTransitioning] = useState(false);
    const roomContinueRef = useRef<(() => void) | null>(null);

    const goToNext = useCallback(() => {
        if (currentStep < TOTAL_STEPS - 1) {
            setCurrentStep((prev) => prev + 1);
        } else if (currentStep === TOTAL_STEPS - 1) {
            roomContinueRef.current?.();
        }
    }, [currentStep]);

    const handleLottieLoad = useCallback((dotLottie: DotLottie) => {
        if (!dotLottie) return;
        dotLottie.addEventListener("complete", () => {
            setLottieComplete(true);
        });
    }, []);

    const handleRoomContinue = useCallback((executeContinue: () => void) => {
        roomContinueRef.current = executeContinue;
    }, []);

    const handleRoomSelectionChange = useCallback((selected: boolean) => {
        setRoomSelected(selected);
    }, []);

    const handleRoomTransitionStart = useCallback(() => {
        setIsRoomTransitioning(true);
    }, []);

    const showBottomBar = currentStep > 0 || lottieComplete;
    const showWordmark = currentStep > 0;
    const isButtonDisabled = currentStep === TOTAL_STEPS - 1 && !roomSelected;

    return (
        <div className="size-full bg-[linear-gradient(180deg,#ECE7EC_0%,#FFF_100%)] overflow-hidden relative">
            {/* ─── Top bar: battery/wifi always, wordmark from slide 1+ ─── */}
            <motion.div
                className="mx-4 flex flex-row items-center justify-between mt-4 z-10 relative shrink-0"
                animate={{ opacity: isRoomTransitioning ? 0 : 1 }}
                transition={{ duration: 0.3 }}
                style={{ height: "21px" }}
            >
                <div className="absolute left-0 flex items-center justify-start gap-x-1">
                    <BatteryIcon className="text-black" />
                    <WifiIcon width={12} height={12} className="text-black" />
                </div>

                <div className="flex grow-1 items-center justify-center">
                    <AnimatePresence>
                        {showWordmark && (
                            <motion.div
                                key="wordmark"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <CashCtrlWordmark />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* ─── Slide content area — grows to fill, clipped ─── */}
            <div className="absolute inset-0 overflow-hidden pb-20">
                {/* Slide 0 */}
                <motion.div
                    className="absolute inset-0"
                    animate={{
                        y: currentStep === 0 ? 0 : "-100%",
                        opacity: currentStep === 0 ? 1 : 0,
                    }}
                    transition={slidePositionTransitionProps}
                >
                    <Slide0 lottieComplete={lottieComplete} onLottieLoad={handleLottieLoad} />
                </motion.div>

                {/* Slide 1 */}
                <motion.div
                    className="absolute inset-0"
                    initial={{ y: "-100%", opacity: 0 }}
                    animate={{
                        y: currentStep === 1 ? 0 : currentStep < 1 ? "100%" : "-100%",
                        opacity: currentStep === 1 ? 1 : 0,
                        scale: currentStep === 1 ? 1 : 0.95,
                    }}
                    transition={slidePositionTransitionProps}
                >
                    <Slide1 isActive={currentStep === 1} />
                </motion.div>

                {/* Slide 2 */}
                <motion.div
                    className="absolute inset-0"
                    initial={{ y: "-100%", opacity: 0 }}
                    animate={{
                        y: currentStep === 2 ? 0 : currentStep < 2 ? "100%" : "-100%",
                        opacity: currentStep === 2 ? 1 : 0,
                        scale: currentStep === 2 ? 1 : 0.95,
                    }}
                    transition={slidePositionTransitionProps}
                    style={{ zIndex: isRoomTransitioning ? 50 : undefined }}
                >
                    <InitialRoomSelect
                        isActive={currentStep === 2}
                        onContinue={handleRoomContinue}
                        onSelectionChange={handleRoomSelectionChange}
                        onTransitionStart={handleRoomTransitionStart}
                    />
                </motion.div>
            </div>

            {/* ─── Persistent bottom bar: dots + button + hint ─── */}
            <AnimatePresence>
                {showBottomBar && !isRoomTransitioning && (
                    <motion.div
                        key="bottom-bar"
                        className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-4 px-4 py-6 z-20"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3, delay: 0.9 }}
                    >
                        {/* Dot pagination */}
                        {/*                        <div className="flex gap-2">
                            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`size-2 rounded-full transition-colors duration-300 ${
                                        i === currentStep ? "bg-secondary-500" : "bg-secondary-500/30"
                                    }`}
                                />
                            ))}
                        </div>*/}

                        <Button theme="secondary" onClick={goToNext} disabled={isButtonDisabled}>
                            <span>Weiter</span>
                        </Button>

                        <p className="text-secondary-300 text-sm text-center">
                            <span className="font-bold">Hinweis: </span>
                            Es ist ein Spiel – keine echten
                            <br />
                            Finanz- oder Anlageempfehlungen.
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default WelcomeSequence;

// ─── Slide 0: Lottie centered → moves up, text appears below ───

type Slide0Props = {
    lottieComplete: boolean;
    onLottieLoad: (dotLottie: DotLottie) => void;
};

const Slide0 = ({ lottieComplete, onLottieLoad }: Slide0Props) => {
    const [animationDone, setAnimationDone] = useState(false);
    const lottieLoadedRef = useRef(false);
    const delayPassedRef = useRef(false);
    const dotLottieRef = useRef<DotLottie | null>(null);

    useEffect(() => {
        if (!lottieComplete) return;
        const timer = setTimeout(() => setAnimationDone(true), 300);
        return () => clearTimeout(timer);
    }, [lottieComplete]);

    const tryPlay = useCallback(() => {
        if (lottieLoadedRef.current && delayPassedRef.current && dotLottieRef.current) {
            dotLottieRef.current.play();
        }
    }, []);

    const handleLottieRef = useCallback(
        (dotLottie: DotLottie) => {
            if (!dotLottie) return;
            dotLottieRef.current = dotLottie;

            dotLottie.addEventListener("load", () => {
                lottieLoadedRef.current = true;
                tryPlay();
            });

            if (dotLottie.isLoaded) {
                lottieLoadedRef.current = true;
            }

            setTimeout(() => {
                delayPassedRef.current = true;
                tryPlay();
            }, 600);

            onLottieLoad(dotLottie);
        },
        [onLottieLoad, tryPlay],
    );

    return (
        <div className="size-full flex flex-col items-center pt-12 px-4 relative">
            <motion.div
                className="w-full flex items-center justify-center h-[54%]"
                initial={false}
                animate={{
                    y: animationDone ? 0 : "calc(54% - 80px)",
                }}
                transition={{ type: "spring", stiffness: 110, damping: 20 }}
            >
                <DotLottieReact src={logoAnimation} autoplay={false} dotLottieRefCallback={handleLottieRef} />
            </motion.div>

            <motion.div
                className="absolute bottom-34 left-0 right-0 flex flex-col items-center text-center gap-2 px-4"
                initial={false}
                animate={{
                    opacity: animationDone ? 1 : 0,
                    y: animationDone ? 0 : 16,
                }}
                transition={{ type: "spring", stiffness: 120, damping: 20, delay: animationDone ? 0.3 : 0 }}
            >
                <h1 className="text-secondary-500 font-bold">Bereit in dein neues Leben zu ziehen?</h1>
                <p></p>
                <p className="text-secondary-300">
                    <span className="text-secondary-500 font-bold">Deine Ziele bis 2046:</span>
                    <br />
                    💸 Niemals broke
                    <br />
                    ❤️ Glücklich sein
                    <br />
                    🏆 Trophäen gewonnen
                </p>
            </motion.div>
        </div>
    );
};

const Slide1 = ({ isActive }: { isActive: boolean }) => {
    const [lottieComplete, setLottieComplete] = useState(false);
    const [lottieReady, setLottieReady] = useState(false);
    const lottieRef = useRef<DotLottie | null>(null);
    const lottieLoadedRef = useRef(false);

    const tryPlay = useCallback(() => {
        if (lottieLoadedRef.current && lottieRef.current) {
            setLottieReady(true);
            lottieRef.current.play();
        }
    }, []);

    const handleLottieRef = useCallback((dotLottie: DotLottie) => {
        if (!dotLottie) return;
        lottieRef.current = dotLottie;

        dotLottie.addEventListener("load", () => {
            lottieLoadedRef.current = true;
        });

        dotLottie.addEventListener("complete", () => {
            setLottieComplete(true);
        });

        if (dotLottie.isLoaded) {
            lottieLoadedRef.current = true;
        }
    }, []);

    useEffect(() => {
        if (!isActive || !lottieRef.current) return;
        if (lottieLoadedRef.current) {
            setTimeout(() => tryPlay(), 400);
        } else {
            // Not loaded yet — wait for load, then play after delay
            const handler = () => {
                lottieLoadedRef.current = true;
                setTimeout(() => tryPlay(), 400);
            };
            lottieRef.current.addEventListener("load", handler);
            return () => {
                lottieRef.current?.removeEventListener("load", handler);
            };
        }
    }, [isActive, tryPlay]);

    return (
        <div className="size-full flex flex-col items-center pt-12 px-4 pb-34 relative">
            <h1 className="text-secondary-500 font-bold text-center leading-[135%]">
                Du wirst viele Entscheidungen
                <br />
                in deinem Leben treffen …
            </h1>

            <div
                className="w-full flex items-center justify-center h-full py-4"
                style={{ opacity: lottieReady ? 1 : 0 }}
            >
                <DotLottieReact src={choicesAnimation} autoplay={false} dotLottieRefCallback={handleLottieRef} />
            </div>

            <motion.p
                className="text-secondary-500 font-bold px-4"
                initial={false}
                animate={{
                    opacity: lottieComplete ? 1 : 0,
                    y: lottieComplete ? 0 : 16,
                }}
                transition={{ type: "spring", stiffness: 100, damping: 10 }}
            >
                ... und die verändern alles.
            </motion.p>
        </div>
    );
};
